import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, CreditCard, FileSpreadsheet, FileText, Lock, Pencil, Search, Users } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Tabs } from '@/components/ui/Tabs'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { brl } from '@/lib/format'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ProjectionService,
  type Projection, type ProjectionItem,
} from '@/services/modules/ProjectionService'

type Tab = 'assinantes' | 'empresas'
type MethodFilter = '' | 'BOLETO' | 'CARTÃO DE CRÉDITO'

/**
 * Projeção das faturas do próximo mês.
 *
 * É a tela pela qual o financeiro confere e ajusta antes do dia 1. Por isso o
 * estado do snapshot fica em destaque: `PREVIA` é acompanhamento e muda todo
 * dia; `SNAPSHOT` é o que a geração vai respeitar — e só ele aceita ajuste.
 */
export function RevenueProjectionPage() {
  const { can } = useAuthority()
  const canAdjust = can('financial.revenues.edit_dates')

  const [month, setMonth] = useState(nextMonthValue())
  const [data, setData] = useState<Projection | null>(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('assinantes')
  const [method, setMethod] = useState<MethodFilter>('')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const [editing, setEditing] = useState<ProjectionItem | null>(null)
  const [form, setForm] = useState({ total: '', dueDate: '', note: '', skipped: false })
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await ProjectionService.get(month))
    } catch (e) {
      toast.error({ title: 'Erro ao carregar', description: extractApiError(e).message })
      setData(null)
    } finally { setLoading(false) }
  }, [month])

  useEffect(() => { void load() }, [load])

  const isSnapshot = data?.run.kind === 'SNAPSHOT'
  const rows = tab === 'assinantes' ? (data?.subscribers ?? []) : (data?.companies ?? [])

  const filtered = useMemo(() => {
    const termo = search.trim().toLowerCase()
    return rows.filter((r) =>
      (!method || r.paymentMethod === method)
      && (!termo || r.name.toLowerCase().includes(termo) || (r.document ?? '').includes(termo)),
    )
  }, [rows, method, search])

  const somaFiltrada = useMemo(
    () => filtered.filter((r) => !r.skipped).reduce((acc, r) => acc + r.total, 0),
    [filtered],
  )

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  async function saveAdjust() {
    if (!editing) return
    setBusy(true)
    try {
      await ProjectionService.adjust(editing.id, {
        total: form.total ? Number(form.total) : undefined,
        dueDate: form.dueDate || undefined,
        skipped: form.skipped,
        note: form.note || undefined,
      })
      toast.success({ title: 'Ajuste salvo', description: 'A geração do dia 1 vai usar este valor.' })
      setEditing(null); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível ajustar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  function exportExcel() {
    if (!filtered.length) { toast.error('Nenhum registro para exportar.'); return }
    const isCompany = tab === 'empresas'
    const columns: ExportColumn<ProjectionItem>[] = [
      { header: isCompany ? 'Empresa' : 'Associado', accessor: (r) => r.name },
      ...(isCompany
        ? [{ header: 'Colaboradores', accessor: (r: ProjectionItem) => r.collaboratorsCount }]
        : [
            { header: 'CPF',   accessor: (r: ProjectionItem) => r.document ?? '' },
            { header: 'Plano', accessor: (r: ProjectionItem) => r.planName ?? '' },
          ]),
      { header: 'Cupom',          accessor: (r) => r.couponCode ?? '' },
      { header: 'Forma',          accessor: (r) => (r.paymentMethod === 'BOLETO' ? 'Boleto' : 'Cartão') },
      { header: 'Vencimento',     accessor: (r) => r.formatted.dueDate ?? '' },
      { header: 'Plano (valor)',  accessor: (r) => brl(r.planAmount) },
      { header: 'Desconto',       accessor: (r) => (r.discount > 0 ? brl(r.discount) : '') },
      { header: 'Total',          accessor: (r) => brl(r.total) },
      { header: 'Comissões',      accessor: (r) => (r.commissionsTotal > 0 ? brl(r.commissionsTotal) : '') },
      { header: 'Situação',       accessor: (r) => [r.skipped ? 'Excluída' : '', r.adjusted ? 'Ajustada' : ''].filter(Boolean).join(', ') },
      { header: 'Motivo do ajuste', accessor: (r) => r.adjustNote ?? '' },
    ]
    exportToXlsx(`projecao-${isCompany ? 'empresas' : 'associados'}-${month}`, filtered, columns)
  }

  const t = data?.run.totals

  return (
    <>
      <PageHeader
        title="Projeção do próximo mês"
        subtitle="As faturas que serão geradas no dia 1"
        crumbs={[{ label: 'Financeiro' }, { label: 'Projeção' }]}
        actions={
          <div className="w-40">
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} placeholder="Competência" />
          </div>
        }
      />

      {loading && <p className="text-sm text-gray-500">Carregando projeção…</p>}

      {!loading && !data && (
        <div className="rounded-lg bg-white p-8 text-center ring-1 ring-gray-200">
          <p className="text-sm text-gray-700">Nenhuma projeção montada para esta competência.</p>
          <p className="mt-1 text-xs text-gray-500">
            A prévia é montada todo dia às 4h30, e o snapshot às 21h do último dia do mês.
          </p>
        </div>
      )}

      {!loading && data && t && (
        <>
          {/* Estado do snapshot — o que decide se isto vale para a cobrança. */}
          <div className={`mb-4 flex flex-wrap items-center gap-3 rounded-lg p-3 ring-1 ${
            isSnapshot ? 'bg-emerald-50 ring-emerald-200' : 'bg-amber-50 ring-amber-200'
          }`}>
            {isSnapshot ? <Lock size={16} className="text-emerald-700" /> : <FileText size={16} className="text-amber-700" />}
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${isSnapshot ? 'text-emerald-800' : 'text-amber-800'}`}>
                {isSnapshot
                  ? 'Snapshot congelado — é exatamente isto que será cobrado no dia 1'
                  : 'Prévia — recalculada todo dia, ainda não vale como contrato'}
              </p>
              <p className={`text-[11px] ${isSnapshot ? 'text-emerald-700' : 'text-amber-700'}`}>
                Gerada em {data.run.formatted.takenAt ?? '—'}
                {!isSnapshot && ' · o snapshot é tirado às 21h do último dia do mês'}
              </p>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Kpi label="Associados" value={String(t.subscribersCount)} sub={brl(t.subscribersTotal)} icon={<Users size={14} />} />
            <Kpi label="Empresas" value={String(t.companiesCount)} sub={brl(t.companiesTotal)} icon={<FileText size={14} />} />
            <Kpi label="Total a faturar" value={brl(t.grandTotal)} highlight />
            <Kpi label="Comissões geradas" value={brl(t.commissionsTotal)} />
            <Kpi
              label="Margem"
              value={brl(t.grandTotal - t.commissionsTotal)}
              sub={t.grandTotal > 0 ? `${(100 - (t.commissionsTotal / t.grandTotal) * 100).toFixed(1)}%` : undefined}
            />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <Tabs
                items={[
                  { id: 'assinantes', label: 'Associados', badge: <Badge variant="gray">{data.subscribers.length}</Badge> },
                  { id: 'empresas', label: 'Empresas', badge: <Badge variant="gray">{data.companies.length}</Badge> },
                ]}
                active={tab}
                onChange={(id) => setTab(id as Tab)}
              />
            </div>
            <div className="w-48">
              <Select
                value={method} onChange={(e) => setMethod(e.target.value as MethodFilter)}
                options={[
                  { value: 'BOLETO', label: 'Boleto' },
                  { value: 'CARTÃO DE CRÉDITO', label: 'Cartão de crédito' },
                ]}
                placeholder="Todas as formas"
              />
            </div>
            <div className="w-64">
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome ou CPF"
                actionRight={<Search size={14} className="text-gray-400" />}
              />
            </div>
            <span className="ml-auto text-sm text-gray-600">
              {filtered.length} fatura(s) · <strong className="text-gray-800">{brl(somaFiltrada)}</strong>
            </span>
            <Button variant="secondary" size="sm" onClick={exportExcel} iconLeft={<FileSpreadsheet size={14} />}>
              Exportar Excel
            </Button>
          </div>

          <div className="overflow-hidden rounded-lg bg-white ring-1 ring-gray-200">
            <div className="max-h-[calc(100vh-420px)] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 text-gray-500">
                  <tr>
                    <th className="w-8" />
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide">Cliente</th>
                    <th className="px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wide">Forma</th>
                    <th className="px-3 py-2 text-center text-[11px] font-medium uppercase tracking-wide">Vencimento</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide">Plano</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide">Desconto</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide">Total</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide">Comissões</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} className="p-6 text-center text-sm text-gray-500">Nenhuma fatura com estes filtros.</td></tr>
                  )}
                  {filtered.map((item) => {
                    const aberto = expanded.has(item.id)
                    return (
                      <>
                        <tr key={item.id} className={`border-t border-gray-100 ${item.skipped ? 'opacity-50' : ''}`}>
                          <td className="pl-2">
                            <button type="button" className="cursor-pointer" onClick={() => toggle(item.id)} aria-label={aberto ? 'Recolher' : 'Expandir'}>
                              {aberto ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">{item.name}</span>
                              {item.skipped && <Badge variant="red">excluída</Badge>}
                              {item.adjusted && <Badge variant="violet">ajustada</Badge>}
                            </div>
                            <span className="text-[11px] text-gray-500">
                              {item.kind === 'empresa'
                                ? `${item.collaboratorsCount} colaborador(es)`
                                : `${item.document ?? ''}${item.planName ? ` · ${item.planName}` : ''}`}
                              {item.couponCode ? ` · cupom ${item.couponCode}` : ''}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant={item.paymentMethod === 'BOLETO' ? 'blue' : 'violet'}>
                              {item.paymentMethod === 'BOLETO'
                                ? 'Boleto'
                                : <span className="inline-flex items-center gap-1"><CreditCard size={10} /> Cartão</span>}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-center tabular-nums text-gray-600">{item.formatted.dueDate}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600">{brl(item.planAmount)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                            {item.discount > 0 ? `−${brl(item.discount)}` : '—'}
                          </td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-gray-800">{brl(item.total)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                            {item.commissionsTotal > 0 ? brl(item.commissionsTotal) : '—'}
                          </td>
                          <td className="pr-2 text-right">
                            {canAdjust && isSnapshot && (
                              <button
                                type="button"
                                className="cursor-pointer"
                                onClick={() => {
                                  setEditing(item)
                                  setForm({ total: String(item.total), dueDate: item.dueDate ?? '', note: item.adjustNote ?? '', skipped: item.skipped })
                                }}
                                aria-label={`Ajustar ${item.name}`}
                              >
                                <Pencil size={13} className="text-gray-400 hover:text-brand-500" />
                              </button>
                            )}
                          </td>
                        </tr>

                        {aberto && (
                          <tr key={`${item.id}-detail`} className="bg-gray-50/60">
                            <td />
                            <td colSpan={8} className="px-3 py-3">
                              <div className="grid gap-4 lg:grid-cols-2">
                                {item.lines.length > 0 && (
                                  <Panel title={`Colaboradores (${item.lines.length})`}>
                                    <div className="max-h-56 overflow-y-auto">
                                      {item.lines.map((l) => (
                                        <div key={l.id} className="flex items-center justify-between border-b border-gray-100 py-1 last:border-0 text-xs">
                                          <div className="min-w-0">
                                            <p className="break-words text-gray-700">{l.name}</p>
                                            <p className="text-[10px] text-gray-500">
                                              {l.planName ?? '—'}
                                              {l.couponCode ? ` · cupom ${l.couponCode}` : ''}
                                              {l.installment ? ` · parcela ${l.installment}` : ''}
                                            </p>
                                          </div>
                                          <div className="text-right tabular-nums">
                                            {l.discount > 0 && <span className="mr-2 text-[10px] text-gray-500">−{brl(l.discount)}</span>}
                                            <span className="text-gray-800">{brl(l.total)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </Panel>
                                )}

                                <Panel title={`Comissões (${item.commissions.length})`}>
                                  {item.commissions.length === 0 && (
                                    <p className="text-xs text-gray-500">Esta fatura não gera comissão.</p>
                                  )}
                                  {item.commissions.map((c) => (
                                    <div key={c.id} className="flex items-center justify-between border-b border-gray-100 py-1 last:border-0 text-xs">
                                      <div className="min-w-0">
                                        <p className="break-words text-gray-700">{c.userName}</p>
                                        <p className="text-[10px] text-gray-500">
                                          {c.type?.toLowerCase() ?? '—'} · {c.paidByCompany ? 'a Ampla paga' : 'repasse do parceiro'}
                                        </p>
                                      </div>
                                      <span className="tabular-nums text-gray-800">{brl(c.amount)}</span>
                                    </div>
                                  ))}
                                </Panel>
                              </div>

                              {item.adjustNote && (
                                <p className="mt-2 text-[11px] text-violet-700">Ajuste: {item.adjustNote}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <Modal
        open={!!editing} onClose={() => setEditing(null)} size="sm"
        title={`Ajustar — ${editing?.name ?? ''}`}
        footer={<>
          <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancelar</Button>
          <Button loading={busy} onClick={() => void saveAdjust()}>Salvar ajuste</Button>
        </>}
      >
        <div className="space-y-3">
          <p className="text-xs text-gray-500">
            O que ficar aqui é o que será cobrado no dia 1 — a geração segue o snapshot, não recalcula.
          </p>
          <Input label="Valor" type="number" step="0.01" value={form.total} placeholder="0,00"
            onChange={(e) => setForm((f) => ({ ...f, total: e.target.value }))} />
          <Input label="Vencimento" type="date" value={form.dueDate} placeholder="dd/mm/aaaa"
            onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
          <Input label="Motivo do ajuste" value={form.note} placeholder="Ex.: desconto negociado"
            hint="Fica registrado junto com a fatura projetada."
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.skipped}
              onChange={(e) => setForm((f) => ({ ...f, skipped: e.target.checked }))} />
            Não gerar esta fatura no dia 1
          </label>
        </div>
      </Modal>
    </>
  )
}

function Kpi({ label, value, sub, icon, highlight }: {
  label: string; value: string; sub?: string; icon?: React.ReactNode; highlight?: boolean
}) {
  return (
    <div className={`rounded-lg p-4 ring-1 ${highlight ? 'bg-brand-50 ring-brand-200' : 'bg-white ring-gray-200'}`}>
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {icon}{label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-gray-800">{value}</p>
      {sub && <p className="text-[11px] tabular-nums text-gray-500">{sub}</p>}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-white p-3 ring-1 ring-gray-200">
      <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400">{title}</p>
      {children}
    </div>
  )
}

/** Competência padrão: o mês que vem, no formato do input `month`. */
function nextMonthValue(): string {
  const d = new Date()
  d.setMonth(d.getMonth() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}
